// Family Yoga Generator - Batch generate yogas for family members using cached data
// Follows caching philosophy: Check database first, only generate if missing

import { supabase } from './supabaseClient';
import { getUserPermanentYogas, storeYogasFromFeeds, type StoredYoga } from './yogaStorage';
import { generateFeeds, type FunctionalClassification } from '../astro/feeds/generateFeedsV2';
import type { DashaContext, TransitContext, HouseNum } from '../astro/feeds/types';

interface FamilyMemberYogaStatus {
  profileId: string;
  fullName: string;
  hasYogas: boolean;
  yogaCount: number;
  needsGeneration: boolean;
}

/**
 * Check which family members have yogas already stored
 */
export async function checkFamilyYogaStatus(
  userId: string,
  profileIds: string[]
): Promise<FamilyMemberYogaStatus[]> {
  if (profileIds.length === 0) return [];

  try {
    // Query user_yogas table to check existing yogas
    const { data: existingYogas, error } = await supabase
      .from('user_yogas')
      .select('birth_profile_id')
      .eq('user_id', userId)
      .in('birth_profile_id', profileIds)
      .eq('category', 'permanent') // Check permanent yogas only
      .eq('is_present', true);

    if (error) throw error;

    // Get profile names
    const { data: profiles } = await supabase
      .from('birth_profiles')
      .select('id, full_name')
      .in('id', profileIds);

    const profileMap = new Map((profiles || []).map(p => [p.id, p.full_name]));
    const yogaCountByProfile = new Map<string, number>();

    (existingYogas || []).forEach(yoga => {
      const count = yogaCountByProfile.get(yoga.birth_profile_id) || 0;
      yogaCountByProfile.set(yoga.birth_profile_id, count + 1);
    });

    return profileIds.map(profileId => ({
      profileId,
      fullName: profileMap.get(profileId) || 'Unknown',
      hasYogas: yogaCountByProfile.has(profileId),
      yogaCount: yogaCountByProfile.get(profileId) || 0,
      needsGeneration: !yogaCountByProfile.has(profileId) || yogaCountByProfile.get(profileId)! < 3
    }));
  } catch (err) {
    console.error('[checkFamilyYogaStatus] Error:', err);
    return profileIds.map(profileId => ({
      profileId,
      fullName: 'Unknown',
      hasYogas: false,
      yogaCount: 0,
      needsGeneration: true
    }));
  }
}

/**
 * Generate yogas for a single family member using their cached chart_data
 */
export async function generateYogasForProfile(
  userId: string,
  profileId: string
): Promise<{ success: boolean; stored: number; errors: string[] }> {
  try {
    console.log(`[generateYogasForProfile] Starting for profile: ${profileId}`);

    // Fetch profile with chart_data
    const { data: profile, error: profileError } = await supabase
      .from('birth_profiles')
      .select('id, full_name, chart_data')
      .eq('id', profileId)
      .single();

    if (profileError) {
      console.error('[generateYogasForProfile] Profile error:', profileError);
      throw profileError;
    }

    console.log(`[generateYogasForProfile] Loaded profile:`, profile.full_name, 'has chart_data:', !!profile.chart_data);

    if (!profile.chart_data) {
      return {
        success: false,
        stored: 0,
        errors: [`Profile ${profile.full_name} has no chart_data. Please calculate their chart first.`]
      };
    }

    const chartData = profile.chart_data;
    console.log(`[generateYogasForProfile] Chart data keys:`, Object.keys(chartData));

    // Extract necessary data from chart_data JSONB
    const placements = chartData.placements || {};
    const houseScores = chartData.houseScores || {};
    const functionalClassification = chartData.functionalClassification || {};
    const combustionMap = chartData.combustionMap || {};
    const vimshottariTimeline = chartData.vimshottariTimeline || [];

    console.log(`[generateYogasForProfile] Extracted - placements: ${Object.keys(placements).length}, houseScores: ${Object.keys(houseScores).length}, timeline: ${vimshottariTimeline.length}`);

    // Build DashaContext from vimshottariTimeline (use first 4 levels)
    if (vimshottariTimeline.length < 4) {
      return {
        success: false,
        stored: 0,
        errors: [`Profile ${profile.full_name} missing dasha timeline data`]
      };
    }

    const transformedDasha: DashaContext = {
      levels: vimshottariTimeline.slice(0, 4).map((dasha: any) => ({
        level: dasha.level as 'MD' | 'AD' | 'PD' | 'SD',
        planet: dasha.planet,
        occupies: dasha.house as HouseNum,
        rules: dasha.rules as HouseNum[],
        aspects: dasha.aspects as HouseNum[],
        from: dasha.from,
        to: dasha.to
      })),
      window: {
        from: vimshottariTimeline[0].from,
        to: vimshottariTimeline[3].to
      },
      houseScores: houseScores as Record<HouseNum, number>
    };

    // Generate feeds using existing generateFeeds function
    const transitCtx: TransitContext = {
      positions: [],
      planetsTransitingDashaOccupied: {} as any
    };

    const feeds = generateFeeds({
      dasha: transformedDasha,
      transits: transitCtx,
      functional: functionalClassification as FunctionalClassification,
      combustionMap: combustionMap || {}
    });

    // Filter natal yogas (permanent yogas) - comprehensive list
    // Includes: classical yogas from rules.ts, mahapurusha yogas, doshas, and raj yogas
    const natalYogaFeeds = feeds.filter(f => {
      const key = f.ruleKey || '';
      
      // Primary filter: any rule ending with '_natal'
      if (key.endsWith('_natal')) return true;
      
      // Secondary filter: yoga-related keys (from classical_yogas.ts and rules.ts)
      if (key.includes('yoga') && !key.startsWith('sade_sati')) return true;
      
      // Tertiary filter: specific dosha patterns
      if (key.includes('dosha') || key.includes('kaal_sarp')) return true;
      
      // Quaternary filter: specific classical combinations
      if (
        key.includes('raj_yoga') ||
        key.includes('dhana') ||
        key.includes('parivartana') ||
        key.includes('neecha_bhanga') ||
        key.includes('viparita') ||
        key.includes('mahapurusha')
      ) return true;
      
      return false;
    });

    // Store in database with birth_profile_id
    const result = await storeYogasFromFeeds(
      natalYogaFeeds,
      userId,
      profileId,
      chartData
    );

    return result;
  } catch (err: any) {
    console.error('[generateYogasForProfile] Error:', err);
    return {
      success: false,
      stored: 0,
      errors: [err?.message || 'Unknown error']
    };
  }
}

/**
 * Batch generate yogas for multiple family members (only those needing generation)
 */
export async function batchGenerateFamilyYogas(
  userId: string,
  profileIds: string[],
  onProgress?: (current: number, total: number, profileName: string) => void
): Promise<{
  success: boolean;
  results: Array<{
    profileId: string;
    profileName: string;
    stored: number;
    skipped: boolean;
    errors: string[];
  }>;
}> {
  try {
    // Check which profiles need generation
    const statusList = await checkFamilyYogaStatus(userId, profileIds);
    const needsGeneration = statusList.filter(s => s.needsGeneration);

    console.log(`[batchGenerateFamilyYogas] ${needsGeneration.length}/${profileIds.length} profiles need yoga generation`);

    const results = [];

    // Generate for profiles that need it
    for (let i = 0; i < needsGeneration.length; i++) {
      const status = needsGeneration[i];
      
      if (onProgress) {
        onProgress(i + 1, needsGeneration.length, status.fullName);
      }

      const result = await generateYogasForProfile(userId, status.profileId);

      results.push({
        profileId: status.profileId,
        profileName: status.fullName,
        stored: result.stored,
        skipped: false,
        errors: result.errors
      });

      // Small delay to avoid overwhelming the database
      if (i < needsGeneration.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Add skipped profiles to results
    statusList
      .filter(s => !s.needsGeneration)
      .forEach(s => {
        results.push({
          profileId: s.profileId,
          profileName: s.fullName,
          stored: s.yogaCount,
          skipped: true,
          errors: []
        });
      });

    return {
      success: results.every(r => r.errors.length === 0),
      results
    };
  } catch (err: any) {
    console.error('[batchGenerateFamilyYogas] Error:', err);
    return {
      success: false,
      results: [{
        profileId: '',
        profileName: 'Unknown',
        stored: 0,
        skipped: false,
        errors: [err?.message || 'Unknown error']
      }]
    };
  }
}

/**
 * Force regenerate yogas for a specific profile (clear cache first)
 */
export async function regenerateYogasForProfile(
  userId: string,
  profileId: string
): Promise<{ success: boolean; stored: number; errors: string[] }> {
  try {
    // Delete existing yogas for this profile
    const { error: deleteError } = await supabase
      .from('user_yogas')
      .delete()
      .eq('user_id', userId)
      .eq('birth_profile_id', profileId);

    if (deleteError) {
      console.error('[regenerateYogasForProfile] Delete error:', deleteError);
    }

    // Generate fresh yogas
    return await generateYogasForProfile(userId, profileId);
  } catch (err: any) {
    console.error('[regenerateYogasForProfile] Error:', err);
    return {
      success: false,
      stored: 0,
      errors: [err?.message || 'Unknown error']
    };
  }
}
